import apiService from '@sonifi/wb-core/services/api.service';
import {
  setSaveInProgress,
  setStaycastBrandingSelected,
  updateStaycastSettings,
} from '../store/brandingSlice';
import { defaultConfig } from '../../../config-env';
import { buildApiUri } from '@sonifi/wb-core/utils/parseHelper';
import first from 'lodash/first';
import transform from 'lodash/transform';
import trim from 'lodash/trim';
import unescape from 'lodash/unescape';
import { setAlert } from '@sonifi/wb-notifier/store/notifierSlice';

const getUrlForAsset = (value, tokenEndpoint) => {
  const unwrappedValue =
    value?.indexOf('url') === 0 || value?.indexOf('!') === 0
      ? trim((value.match(/(?:\()([^)]+)(?:\))/) || []).pop(), '"\'')
      : value;

  if (
    (unwrappedValue &&
      (unwrappedValue.indexOf('data') === 0 ||
        unwrappedValue.indexOf('#') === 0)) ||
    unwrappedValue.startsWith('http')
  ) {
    return unwrappedValue;
  }

  return buildApiUri(tokenEndpoint, unwrappedValue);
};

export const translateTokenResponse = (tokenEndpoint, resp) => {
  if (resp?.data) {
    const altId =
      resp.data.defaultAlternative ||
      first(Object.keys(resp.data.alternatives));
    const tokens = transform(
      resp.data.alternatives[altId],
      (acc, rawValue, rawKey) => {
        const key = rawKey.replace(/\$/g, '');
        let value = rawValue;

        if (
          key === 'appIcon' ||
          key === 'footerBackground' ||
          key === 'background' ||
          key === 'brandingImage'
        ) {
          value = getUrlForAsset(value, tokenEndpoint);
        } else {
          if (key === 'supportLink') {
            value = trim((value.match(/(?:\[)[^)]+(?:\])/) || []).pop(), '[]');
          }

          // NOTE: we unescape text values because the backend forces any values sent to it to be escaped
          //       therefore, we don't want it to escape the values again (plus, we don't want to display
          //       escaped values to the user in input fields)
          value = unescape(value);
        }

        acc[key] = value;
        return acc;
      },
      {},
    );

    tokens.parent = (first(resp.data.extends) || '').split('/')[0];
    tokens.alternative = altId;
    return tokens;
  }
  return {};
};

const getStaycastDefaults = async (cmsId) => {
  const urlToken = buildApiUri(
    defaultConfig.staycastApiUri,
    'welcome',
    'settings',
    'tokens',
    cmsId,
  );
  const res = await apiService.getRaw(urlToken);
  const url = buildApiUri(
    defaultConfig.staycastApiUri,
    'welcome',
    'settings',
    'tokens',
    'defaults',
    res.data.defaultAlternative,
  );
  const resDefault = await apiService.getRaw(url);
  return {
    settings: translateTokenResponse(defaultConfig.staycastApiUri, res),
    parentSettings: translateTokenResponse(
      defaultConfig.staycastApiUri,
      resDefault,
    ),
  };
};

export const initStaycastBranding = (groupId) => async (dispatch, getState) => {
  try {
    const {
      groups: { groups },
    } = getState();
    // TODO: selectedGroup should be fine instead of allGroups?
    const group = groups.allGroups.find((g) => g.id === groupId);
    const { settings, parentSettings } = await getStaycastDefaults(
      group.cms_id,
    );

    await Promise.all([
      dispatch(
        setStaycastBrandingSelected({
          settings,
          parentSettings,
        }),
      ),
    ]);
  } catch (error) {
    console.error(error);
    // TODO: handle error??
    // return Promise.reject(error);
  }
};

const buildTokensForUpdateRequest = (tokens) => {
  const form = new FormData();
  for (const [key, value] of Object.entries(tokens)) {
    if (value && typeof value !== 'string') {
      // It's a Filelist
      form.append(key, value[0]);
    } else {
      form.append(key, value);
    }
  }
  return form;
};

const translateTokenError = (resp) => {
  let error = resp?.data?.error;

  if (error && typeof error === 'string') {
    const field = (error.match(/for "([^"]+)" field/) || []).pop();

    if (field) {
      const translatedField = {
        appIcon: 'App Icon',
        appName: 'App Name',
        supportLink: 'Support Website',
        supportPhone: 'Support Phone Number',
        background: 'Background',
        brandingImage: 'Branding Image',
        footerBackground: 'Hotel Image',
      }[field];
      error = [
        {
          field: translatedField,
          error: error.replace(field, translatedField),
        },
      ];
    } else {
      error = [{ field: 'staycast', error }];
    }
  }

  return error;
};

export const saveStaycastConfig =
  ({ settings, groupId }) =>
  async (dispatch, getState) => {
    dispatch(setSaveInProgress(true));
    const {
      groups: { groups },
    } = getState();
    const group = groups.allGroups.find((g) => g.id === parseInt(groupId));
    try {
      const url = buildApiUri(
        defaultConfig.staycastApiUri,
        'welcome',
        'settings',
        'tokens',
        group.cms_id,
      );
      const data = buildTokensForUpdateRequest(settings);

      const res = await apiService.patchRaw(url, data);
      const settingsUpdated = translateTokenResponse(
        defaultConfig.staycastApiUri,
        res,
      );
      dispatch(
        setAlert({
          title: 'Settings saved',
          content:
            'The settings for this property have been successfully saved.',
          type: 'success',
          show: true,
          dismissable: true,
        }),
      );
      await Promise.all([
        dispatch(updateStaycastSettings({ settings: settingsUpdated })),
        dispatch(setSaveInProgress(false)),
      ]);
      return true;
    } catch (error) {
      console.error(error);
      const errorTranslated = translateTokenError(error.response);
      if (errorTranslated && Array.isArray(errorTranslated)) {
        const errors = errorTranslated.map(
          (err) => `<li>${err.field}: ${err.error}</li>`,
        );
        dispatch(
          setAlert({
            title: 'Settings save failed',
            content: `The settings for this property could not be saved. Error(s) returned by server: <ul>${errors}</ul>`,
            type: 'danger',
            show: true,
            dismissable: true,
          }),
        );
      } else {
        dispatch(
          setAlert({
            title: 'Settings save failed',
            content: 'The settings for this property could not be saved.',
            type: 'danger',
            show: true,
            dismissable: true,
          }),
        );
      }
      dispatch(setSaveInProgress(false));
      return false;
    }
  };
